/**
 * Booking capacity: total seats, the increment they are sold in, and the minimum a guest
 * must take.
 *
 * These three numbers decide what a guest is allowed to book, so the same rules have to
 * hold in the provider's form, in the guest's seat picker, and on the server when a
 * booking is actually made. This module is the only place they are written down.
 *
 * It is pure — no React, no network, no dates — so it can be read in all three places and
 * property-tested on its own. `server/api-util/bookingCapacity.js` mirrors it for the API,
 * and `bookingCapacity.contract.test.js` holds the two to the same answers.
 *
 * The rules, once:
 *   1. totalSeats      >= 1
 *   2. bookingIncrement in 1..totalSeats
 *   3. minimumBooking   a multiple of bookingIncrement, >= bookingIncrement, <= totalSeats
 *   4. a booking        a multiple of bookingIncrement, >= minimumBooking, <= seats left
 */

// A listing that predates this feature has seats and nothing else, which is exactly the
// same thing as "any quantity, one at a time".
export const DEFAULT_BOOKING_INCREMENT = 1;
export const DEFAULT_MINIMUM_BOOKING = 1;

const toPositiveInt = (value, fallback) => {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n >= 1 ? n : fallback;
};

/**
 * Read a capacity off whatever shape it arrives in, and make it legal.
 *
 * Everything else in this module assumes a normalized capacity, so call this at the edges:
 * on a slot read from Mongo, on a publicData entry, on a form's values. Missing or broken
 * values fall back to the pre-feature behaviour rather than throwing — an old listing must
 * keep working, and a bad write must not take a session off sale.
 */
export const normalizeCapacity = (raw = {}) => {
  const totalSeats = toPositiveInt(raw.totalSeats ?? raw.seats, 1);

  // An increment larger than the session itself cannot be satisfied, so it comes down to
  // the capacity rather than making the session unbookable.
  let bookingIncrement = toPositiveInt(raw.bookingIncrement, DEFAULT_BOOKING_INCREMENT);
  if (bookingIncrement > totalSeats) bookingIncrement = totalSeats;

  // The minimum has to land on an increment boundary and fit in the session.
  let minimumBooking = toPositiveInt(raw.minimumBooking, DEFAULT_MINIMUM_BOOKING);
  if (minimumBooking < bookingIncrement) minimumBooking = bookingIncrement;
  minimumBooking = Math.ceil(minimumBooking / bookingIncrement) * bookingIncrement;
  if (minimumBooking > totalSeats) {
    minimumBooking = largestMultipleWithin(bookingIncrement, totalSeats);
  }

  return { totalSeats, bookingIncrement, minimumBooking };
};

// The biggest multiple of `step` that still fits in `limit`, never below one step.
const largestMultipleWithin = (step, limit) => Math.max(step, Math.floor(limit / step) * step);

/** Every increment a provider may choose for a session of this size: 1..totalSeats. */
export const incrementOptions = totalSeats => {
  const total = toPositiveInt(totalSeats, 1);
  return Array.from({ length: total }, (_, i) => i + 1);
};

/**
 * Every minimum a provider may choose, given the increment.
 *
 * Multiples of the increment, up to the capacity — so 20 seats in 3s offers
 * 3, 6, 9, 12, 15, 18, and never 4, 5, 7 or 20.
 */
export const minimumOptions = (totalSeats, bookingIncrement) => {
  const total = toPositiveInt(totalSeats, 1);
  const step = Math.min(toPositiveInt(bookingIncrement, 1), total);
  const out = [];
  for (let v = step; v <= total; v += step) out.push(v);
  return out.length ? out : [step];
};

/**
 * Settle a capacity after the provider changes one of the three fields.
 *
 * The fields depend on each other in one direction — total seats, then increment, then
 * minimum — so a change upstream can leave the ones below it illegal. Rather than refusing
 * the edit, the invalid value moves to the nearest legal one and the caller can say so.
 *
 * Changing the minimum settles to itself: it is the last field, with nothing below it.
 */
export const applyCapacityChange = (previous, change) => {
  const prev = normalizeCapacity(previous);
  const next = { ...prev, ...change };

  const totalSeats = toPositiveInt(next.totalSeats, 1);

  // An increment that no longer fits comes down to the new capacity.
  let bookingIncrement = toPositiveInt(next.bookingIncrement, prev.bookingIncrement);
  if (bookingIncrement > totalSeats) bookingIncrement = totalSeats;

  // The minimum keeps the provider's intent where it can: hold the chosen value if it is
  // still legal, otherwise take the next legal value up, and only come down if that will
  // not fit.
  const wanted = toPositiveInt(next.minimumBooking, prev.minimumBooking);
  let minimumBooking;
  if (wanted % bookingIncrement === 0 && wanted >= bookingIncrement && wanted <= totalSeats) {
    minimumBooking = wanted;
  } else {
    const roundedUp = Math.ceil(Math.max(wanted, bookingIncrement) / bookingIncrement) * bookingIncrement;
    minimumBooking =
      roundedUp <= totalSeats ? roundedUp : largestMultipleWithin(bookingIncrement, totalSeats);
  }

  return { totalSeats, bookingIncrement, minimumBooking };
};

/**
 * How many seats are still open on a session.
 *
 * A seat held by a request awaiting an answer is not available — it is released if that
 * request is declined or expires, not before.
 */
export const remainingSeats = (capacity, reservedSeats = 0) => {
  const { totalSeats } = normalizeCapacity(capacity);
  const taken = Math.max(0, Math.floor(Number(reservedSeats) || 0));
  return Math.max(0, totalSeats - taken);
};

/**
 * The quantities a guest may actually pick, given what is left.
 *
 * This is the list the seat picker shows. It is empty when the seats still open cannot
 * satisfy the minimum — which is the difference between a session having capacity and a
 * session being bookable.
 */
export const bookingQuantityOptions = (capacity, reservedSeats = 0) => {
  const { bookingIncrement, minimumBooking } = normalizeCapacity(capacity);
  const left = remainingSeats(capacity, reservedSeats);
  const out = [];
  for (let q = minimumBooking; q <= left; q += bookingIncrement) out.push(q);
  return out;
};

/** Whether anyone can still book this session at all. */
export const isBookable = (capacity, reservedSeats = 0) =>
  bookingQuantityOptions(capacity, reservedSeats).length > 0;

/**
 * Check one requested quantity, and say why if it fails.
 *
 * The server uses this to answer a booking request, so the reason is written for a person:
 * it is what the guest will be shown when their request is turned down.
 */
export const validateBookingQuantity = (capacity, quantity, reservedSeats = 0) => {
  const { bookingIncrement, minimumBooking } = normalizeCapacity(capacity);
  const left = remainingSeats(capacity, reservedSeats);
  const q = Number(quantity);

  if (!Number.isInteger(q) || q < 1) {
    return { valid: false, code: 'not-a-seat-count', message: 'Choose how many seats you want.' };
  }
  if (q < minimumBooking) {
    return {
      valid: false,
      code: 'below-minimum',
      message: `This session is booked in groups of at least ${minimumBooking}.`,
    };
  }
  if (q % bookingIncrement !== 0) {
    return {
      valid: false,
      code: 'not-a-multiple',
      message: `Seats for this session are booked in multiples of ${bookingIncrement}.`,
    };
  }
  if (q > left) {
    return {
      valid: false,
      code: 'over-capacity',
      message: left
        ? `Only ${left} seat${left === 1 ? '' : 's'} left on this session.`
        : 'This session is full.',
    };
  }
  return { valid: true };
};

/**
 * The sentence under the three fields, in the provider's own terms.
 *
 * It is the only feedback that tells a provider what they have just configured, so it says
 * the whole rule rather than echoing the numbers back.
 */
export const describeCapacity = capacity => {
  const { totalSeats, bookingIncrement, minimumBooking } = normalizeCapacity(capacity);
  const seatWord = n => `${n} seat${n === 1 ? '' : 's'}`;

  // Both rules, every time, in the same order. A provider reading two sessions side by
  // side should not have to work out whether a missing clause means "no minimum" or
  // "the minimum happens to equal the increment".
  return `Customers must book at least ${seatWord(
    minimumBooking
  )}, in increments of ${bookingIncrement}, up to a maximum capacity of ${seatWord(totalSeats)}.`;
};
